// Case Management - Communications Tab
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  Phone,
  Mail,
  MessageSquare,
  Video,
  Users,
  FileText,
  Mic,
  MicOff,
  Square,
  Play,
  Pause,
  Clock,
  Calendar,
  ArrowDownLeft,
  ArrowUpRight,
  Plus,
  Brain,
  Sparkles,
  Loader2,
  Trash2,
  Edit,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Filter,
  Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Communication type icons and labels
const commTypeConfig = {
  phone_call: { icon: Phone, label: 'Phone Call', color: 'bg-blue-100 text-blue-700' },
  email: { icon: Mail, label: 'Email', color: 'bg-purple-100 text-purple-700' },
  text: { icon: MessageSquare, label: 'Text/SMS', color: 'bg-green-100 text-green-700' },
  meeting: { icon: Users, label: 'In-Person Meeting', color: 'bg-amber-100 text-amber-700' },
  video_call: { icon: Video, label: 'Video Call', color: 'bg-pink-100 text-pink-700' },
  letter: { icon: FileText, label: 'Letter', color: 'bg-slate-100 text-slate-700' },
  court_appearance: { icon: FileText, label: 'Court Appearance', color: 'bg-red-100 text-red-700' },
  other: { icon: MessageSquare, label: 'Other', color: 'bg-gray-100 text-gray-700' }
};

export default function CaseCommunicationsTab({ caseId, caseData }) {
  const { token } = useAuth();
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showRecordingDialog, setShowRecordingDialog] = useState(false);
  const [selectedComm, setSelectedComm] = useState(null);
  const [expandedComm, setExpandedComm] = useState(null);
  
  // Filter state
  const [filterType, setFilterType] = useState('all');
  const [filterDirection, setFilterDirection] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // New communication form
  const [formData, setFormData] = useState({
    communication_type: 'phone_call',
    direction: 'outbound',
    subject: '',
    description: '',
    contact_name: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    duration_minutes: ''
  });
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [wiretapAcknowledged, setWiretapAcknowledged] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchCommunications();
  }, [caseId, token]);

  const fetchCommunications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/communications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCommunications(data);
      }
    } catch (error) {
      console.error('Failed to fetch communications:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  // Recording functions
  const startRecording = async () => {
    if (!wiretapAcknowledged) {
      toast.error('Please acknowledge the recording notice first');
      return;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
      
    } catch (error) {
      toast.error('Failed to access microphone. Please allow microphone access.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const saveRecording = async () => {
    if (!audioBlob) return;
    setSaving(true);
    
    try {
      // Create communication first
      const commRes = await fetch(`${API_URL}/api/case-management/cases/${caseId}/communications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          case_id: caseId,
          communication_type: 'phone_call',
          direction: 'outbound',
          subject: 'Voice Recording',
          description: `Recorded call - Duration: ${formatTime(recordingTime)}`,
          contact_name: formData.contact_name || 'Unknown',
          date: new Date().toISOString().split('T')[0],
          time: new Date().toTimeString().slice(0, 5),
          duration_minutes: Math.ceil(recordingTime / 60),
          has_recording: true
        })
      });
      
      if (!commRes.ok) {
        toast.error('Failed to create communication');
        return;
      }
      
      const comm = await commRes.json();
      
      // Upload audio
      const formDataUpload = new FormData();
      formDataUpload.append('audio', audioBlob, 'recording.webm');
      
      const uploadRes = await fetch(`${API_URL}/api/case-management/communications/${comm.id}/upload-audio`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      if (uploadRes.ok) {
        toast.success('Recording saved successfully');
        setShowRecordingDialog(false);
        setAudioBlob(null);
        setRecordingTime(0);
        setWiretapAcknowledged(false);
        setFormData(prev => ({ ...prev, contact_name: '' }));
        fetchCommunications();
      } else {
        toast.error('Failed to upload recording');
      }
      
    } catch (error) {
      toast.error('Failed to save recording');
    } finally {
      setSaving(false);
    }
  };

  const createCommunication = async () => {
    if (!formData.description) {
      toast.error('Please add a description');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/communications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          case_id: caseId,
          duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null
        })
      });
      
      if (res.ok) {
        toast.success('Communication logged');
        setShowNewDialog(false);
        setFormData({
          communication_type: 'phone_call',
          direction: 'outbound',
          subject: '',
          description: '',
          contact_name: '',
          date: new Date().toISOString().split('T')[0],
          time: new Date().toTimeString().slice(0, 5),
          duration_minutes: ''
        });
        fetchCommunications();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to log communication');
      }
    } catch (error) {
      toast.error('Failed to log communication');
    } finally {
      setSaving(false);
    }
  };

  const transcribeCommunication = async (commId) => {
    setTranscribing(true);
    setSelectedComm(commId);
    
    try {
      const res = await fetch(`${API_URL}/api/case-management/communications/${commId}/transcribe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Transcription complete');
        fetchCommunications();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Transcription failed');
      }
    } catch (error) {
      toast.error('Failed to transcribe');
    } finally {
      setTranscribing(false);
      setSelectedComm(null);
    }
  };

  const analyzeCommunication = async (commId) => {
    setAnalyzing(true);
    setSelectedComm(commId);
    
    try {
      const res = await fetch(`${API_URL}/api/case-management/communications/${commId}/analyze`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('AI analysis complete');
        fetchCommunications();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Analysis failed');
      }
    } catch (error) {
      toast.error('Failed to analyze');
    } finally {
      setAnalyzing(false);
      setSelectedComm(null);
    }
  };

  const createTimeEntry = async (comm) => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/communications/${comm.id}/create-time-entry`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Time entry created');
        fetchCommunications();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create time entry');
      }
    } catch (error) {
      toast.error('Failed to create time entry');
    }
  };

  const deleteCommunication = async (commId) => {
    if (!window.confirm('Delete this communication log?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/case-management/communications/${commId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Communication deleted');
        fetchCommunications();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Filter communications
  const filteredComms = communications.filter(comm => {
    if (filterType !== 'all' && comm.communication_type !== filterType) return false;
    if (filterDirection !== 'all' && comm.direction !== filterDirection) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        (comm.subject || '').toLowerCase().includes(query) ||
        (comm.description || '').toLowerCase().includes(query) ||
        (comm.contact_name || '').toLowerCase().includes(query) ||
        (comm.transcription || '').toLowerCase().includes(query)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Loading communications...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Action Bar */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2">
          <Button onClick={() => setShowNewDialog(true)} data-testid="log-communication-btn">
            <Plus className="w-4 h-4 mr-2" />
            Log Communication
          </Button>
          <Button variant="outline" onClick={() => setShowRecordingDialog(true)} data-testid="record-call-btn">
            <Mic className="w-4 h-4 mr-2" />
            Record Call
          </Button>
        </div>
        
        {/* Filters */}
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-48"
              data-testid="search-communications"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40" data-testid="filter-type">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="phone_call">Phone Calls</SelectItem>
              <SelectItem value="email">Emails</SelectItem>
              <SelectItem value="text">Text/SMS</SelectItem>
              <SelectItem value="meeting">Meetings</SelectItem>
              <SelectItem value="video_call">Video Calls</SelectItem>
              <SelectItem value="letter">Letters</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterDirection} onValueChange={setFilterDirection}>
            <SelectTrigger className="w-36" data-testid="filter-direction">
              <SelectValue placeholder="Direction" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="inbound">Inbound</SelectItem>
              <SelectItem value="outbound">Outbound</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Communications List */}
      {filteredComms.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <MessageSquare className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Communications Logged</h3>
            <p className="text-muted-foreground mb-6">
              Start logging calls, emails, and meetings to keep a complete record of client interactions.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => setShowNewDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Log Communication
              </Button>
              <Button variant="outline" onClick={() => setShowRecordingDialog(true)}>
                <Mic className="w-4 h-4 mr-2" />
                Record Call
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredComms.map((comm) => {
            const TypeIcon = commTypeConfig[comm.communication_type]?.icon || MessageSquare;
            const typeColor = commTypeConfig[comm.communication_type]?.color || 'bg-gray-100 text-gray-700';
            const typeLabel = commTypeConfig[comm.communication_type]?.label || comm.communication_type;
            const isExpanded = expandedComm === comm.id;
            
            return (
              <Card key={comm.id} className="hover:shadow-md transition-shadow" data-testid={`comm-${comm.id}`}>
                <CardContent className="p-4">
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded-lg ${typeColor}`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{typeLabel}</span>
                          {comm.direction === 'inbound' ? (
                            <Badge variant="outline" className="text-xs">
                              <ArrowDownLeft className="w-3 h-3 mr-1" />
                              Inbound
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">
                              <ArrowUpRight className="w-3 h-3 mr-1" />
                              Outbound
                            </Badge>
                          )}
                          {comm.has_recording && (
                            <Badge variant="secondary" className="text-xs">
                              <Mic className="w-3 h-3 mr-1" />
                              Recorded
                            </Badge>
                          )}
                          {comm.is_billable && (
                            <Badge className="text-xs bg-emerald-100 text-emerald-700">
                              <DollarSign className="w-3 h-3 mr-1" />
                              Billed
                            </Badge>
                          )}
                        </div>
                        {comm.subject && (
                          <p className="font-medium mt-1">{comm.subject}</p>
                        )}
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {comm.description}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          {comm.contact_name && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {comm.contact_name}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(comm.date).toLocaleDateString()}
                            {comm.time && ` at ${comm.time}`}
                          </span>
                          {comm.duration_minutes && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDuration(comm.duration_minutes)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      {comm.has_recording && !comm.transcription && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => transcribeCommunication(comm.id)}
                          disabled={transcribing && selectedComm === comm.id}
                          title="Transcribe Recording"
                        >
                          {transcribing && selectedComm === comm.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <FileText className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      {comm.transcription && !comm.ai_summary && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => analyzeCommunication(comm.id)}
                          disabled={analyzing && selectedComm === comm.id}
                          title="AI Analysis"
                        >
                          {analyzing && selectedComm === comm.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Brain className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      {!comm.is_billable && comm.duration_minutes > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => createTimeEntry(comm)}
                          title="Create Time Entry"
                        >
                          <DollarSign className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedComm(isExpanded ? null : comm.id)}
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteCommunication(comm.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t space-y-4">
                      {comm.transcription && (
                        <div>
                          <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Transcription
                          </h4>
                          <p className="text-sm bg-muted/50 p-3 rounded-lg whitespace-pre-wrap">
                            {comm.transcription}
                          </p>
                        </div>
                      )}
                      {comm.ai_summary && (
                        <div>
                          <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-purple-500" />
                            AI Summary
                          </h4>
                          <div className="text-sm bg-purple-50 p-3 rounded-lg whitespace-pre-wrap">
                            {comm.ai_summary}
                          </div>
                        </div>
                      )}
                      {comm.has_recording && (
                        <div>
                          <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                            <Mic className="w-4 h-4" />
                            Recording
                          </h4>
                          <audio
                            controls
                            src={`${API_URL}/api/case-management/communications/${comm.id}/recording?token=${token}`}
                            className="w-full"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* New Communication Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Communication</DialogTitle>
            <DialogDescription>
              Record details of a call, email, meeting, or other client interaction.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select
                  value={formData.communication_type}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, communication_type: v }))}
                >
                  <SelectTrigger data-testid="comm-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone_call">Phone Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="text">Text/SMS</SelectItem>
                    <SelectItem value="meeting">In-Person Meeting</SelectItem>
                    <SelectItem value="video_call">Video Call</SelectItem>
                    <SelectItem value="letter">Letter</SelectItem>
                    <SelectItem value="court_appearance">Court Appearance</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Direction</Label>
                <Select
                  value={formData.direction}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, direction: v }))}
                >
                  <SelectTrigger data-testid="comm-direction-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inbound">Inbound</SelectItem>
                    <SelectItem value="outbound">Outbound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Contact Name</Label>
              <Input
                value={formData.contact_name}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                placeholder="Who was this communication with?"
                data-testid="comm-contact-input"
              />
            </div>
            
            <div>
              <Label>Subject</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Brief subject line"
                data-testid="comm-subject-input"
              />
            </div>
            
            <div>
              <Label>Description / Notes *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the communication, key points discussed, action items..."
                rows={4}
                data-testid="comm-description-input"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  data-testid="comm-date-input"
                />
              </div>
              <div>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                  data-testid="comm-time-input"
                />
              </div>
              <div>
                <Label>Duration (min)</Label>
                <Input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: e.target.value }))}
                  placeholder="Minutes"
                  data-testid="comm-duration-input"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createCommunication} disabled={saving} data-testid="save-comm-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Log Communication
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voice Recording Dialog */}
      <Dialog open={showRecordingDialog} onOpenChange={setShowRecordingDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Call</DialogTitle>
            <DialogDescription>
              Record a phone call or voice note. The recording can be transcribed and analyzed by AI.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Wiretap Consent */}
            {!audioBlob && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="wiretap"
                    checked={wiretapAcknowledged}
                    onCheckedChange={setWiretapAcknowledged}
                    data-testid="wiretap-checkbox"
                  />
                  <label htmlFor="wiretap" className="text-sm text-amber-800 cursor-pointer">
                    <strong>Recording Consent Notice:</strong> I acknowledge that recording conversations 
                    may be subject to federal and state wiretapping laws. I confirm that all parties 
                    to this conversation have been notified and have consented to this recording, 
                    or that I am in a one-party consent jurisdiction.
                  </label>
                </div>
              </div>
            )}
            
            {/* Contact Name */}
            <div>
              <Label>Contact Name (Optional)</Label>
              <Input
                value={formData.contact_name}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                placeholder="Who is this call with?"
                disabled={isRecording}
                data-testid="recording-contact-input"
              />
            </div>
            
            {/* Recording Controls */}
            <div className="text-center py-6">
              <div className="text-4xl font-mono mb-4" data-testid="recording-time">
                {formatTime(recordingTime)}
              </div>
              
              {!audioBlob ? (
                <div className="flex justify-center gap-4">
                  {isRecording ? (
                    <Button variant="destructive" size="lg" onClick={stopRecording} data-testid="stop-recording-btn">
                      <Square className="w-5 h-5 mr-2" />
                      Stop Recording
                    </Button>
                  ) : (
                    <Button
                      size="lg"
                      onClick={startRecording}
                      disabled={!wiretapAcknowledged}
                      data-testid="start-recording-btn"
                    >
                      <Mic className="w-5 h-5 mr-2" />
                      Start Recording
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <audio
                    controls
                    src={URL.createObjectURL(audioBlob)}
                    className="w-full"
                  />
                  <div className="flex justify-center gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setAudioBlob(null);
                        setRecordingTime(0);
                      }}
                    >
                      Record Again
                    </Button>
                    <Button onClick={saveRecording} disabled={saving} data-testid="save-recording-btn">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Save Recording
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
